/**
 * PRODUCING THE NEXT SETTINGS DOCUMENT.
 *
 * The permissions document is written with a full REPLACE, not a merge, so
 * every write has to respread the two halves it is not changing or they are
 * silently lost - and the failure only surfaces later, when somebody notices
 * their locks have reset. Two dialogs each hand-rolling that respread is twice
 * the chance of dropping the half it is not looking at, so neither of them is
 * allowed to know how.
 *
 * These are the pure half of what `writeHouseRule`/`writeLock`/`writeOverride`
 * used to do inline: they compute the next document and return it. The write
 * itself belongs to whoever owns the backend. Split out in v8.90.0 so the
 * respread rule is testable without a Firestore mock.
 */
import { DEFAULT_LOCKS } from '../constants';
import { accountOf, overridesFor } from './overrides';
import type { PermissionsDoc } from './permissions';

/** Set ONE house rule, carrying locks and overrides through untouched. */
export function withHouseRule(
  permissions: PermissionsDoc | null | undefined,
  key: string,
  value: unknown,
): PermissionsDoc {
  return {
    locks: { ...DEFAULT_LOCKS, ...(permissions?.locks || {}) },
    playerOverrides: permissions?.playerOverrides || {},
    houseRules: { ...(permissions?.houseRules || {}), [key]: value },
  } as PermissionsDoc;
}

/** Set ONE global lock, carrying house rules and overrides through untouched. */
export function withLock(permissions: PermissionsDoc | null | undefined, key: string, locked: boolean): PermissionsDoc {
  return {
    locks: { ...DEFAULT_LOCKS, ...(permissions?.locks || {}), [key]: locked },
    playerOverrides: permissions?.playerOverrides || {},
    houseRules: permissions?.houseRules || {},
  } as PermissionsDoc;
}

/**
 * Grant or withhold ONE feature for ONE player, named by any of their seats.
 *
 * Filed under the seat's ACCOUNT, the only name the rules know - see
 * `overrides.ts`. An entry an older build filed under this seat id is
 * folded into the account's and removed, so the document converges.
 *
 * `locked` is `null` for "whatever the global lock says" - and the player's
 * entry is removed entirely once nothing is left in it, so a grant that has
 * been handed back does not sit in the document as an empty object.
 */
export function withOverride(
  permissions: PermissionsDoc | null | undefined,
  seatId: string,
  key: string,
  locked: boolean | null,
): PermissionsDoc {
  const account = accountOf(seatId);
  const overrides: Record<string, Record<string, boolean>> = { ...(permissions?.playerOverrides || {}) };
  const forPlayer = overridesFor(permissions, seatId);
  delete overrides[seatId];
  if (locked === null) delete forPlayer[key];
  else forPlayer[key] = locked;
  if (Object.keys(forPlayer).length === 0) delete overrides[account];
  else overrides[account] = forPlayer;
  return {
    locks: { ...DEFAULT_LOCKS, ...(permissions?.locks || {}) },
    playerOverrides: overrides,
    houseRules: permissions?.houseRules || {},
  } as PermissionsDoc;
}
